import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatTimeAgo, getDiscordAvatarUrl, extractYouTubeId, getYouTubeThumbnail } from '../lib/timeUtils';
import { fetchVideos, likeVideo } from '../lib/api';

const ShameWall = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');

  useEffect(() => {
    const loadVideos = async () => {
      try {
        setLoading(true);
        const data = await fetchVideos('shame-wall');
        
        // Ordenar vídeos
        let sortedVideos = [...(data || [])];
        switch (sortBy) {
          case 'views':
            sortedVideos.sort((a, b) => (b.views_count || 0) - (a.views_count || 0));
            break;
          case 'likes':
            sortedVideos.sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0));
            break;
          case 'trending':
            // Combinar likes e views para trending
            sortedVideos.sort((a, b) => {
              const scoreA = (a.likes_count || 0) * 2 + (a.views_count || 0);
              const scoreB = (b.likes_count || 0) * 2 + (b.views_count || 0);
              return scoreB - scoreA;
            });
            break;
          default: // recent
            sortedVideos.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        }

        // Filtrar por busca
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          sortedVideos = sortedVideos.filter(video => 
            video.title?.toLowerCase().includes(query) ||
            video.author_username?.toLowerCase().includes(query) ||
            video.description?.toLowerCase().includes(query)
          );
        }

        setVideos(sortedVideos);
        
        // Definir vídeo em destaque (primeiro da lista ordenada por trending)
        if (sortedVideos.length > 0) {
          const trending = [...data].sort((a, b) => {
            const scoreA = (a.likes_count || 0) * 2 + (a.views_count || 0);
            const scoreB = (b.likes_count || 0) * 2 + (b.views_count || 0);
            return scoreB - scoreA;
          });
          setFeaturedVideo(trending[0]);
        }
      } catch (error) {
        console.error('Erro ao carregar vídeos:', error);
      } finally {
        setLoading(false);
      }
    };

    loadVideos();
  }, [sortBy, searchQuery]);

  const handleVideoClick = (videoId) => {
    navigate(`/video/shame-wall/${videoId}`);
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a14] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a14] text-white" style={{ position: 'relative' }}>
      {/* Background Effects */}
      <div 
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background: `
            radial-gradient(circle at 10% 20%, rgba(139, 92, 246, 0.08) 0%, transparent 40%),
            radial-gradient(circle at 90% 80%, rgba(99, 102, 241, 0.08) 0%, transparent 40%)
          `
        }}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-[#0a0a14]/90 backdrop-blur-[20px] border-b border-purple-500/20 px-10 py-4 flex justify-between items-center z-[1000]">
        <Link to="/feed" className="flex items-center gap-3 text-2xl font-extrabold text-white no-underline">
          <img src="/oasis-logo.ico" alt="Oasis" className="w-10 h-10 rounded-[10px]" />
          <span>OASIS</span>
        </Link>
        <nav className="flex gap-8">
          <Link to="/feed" className="text-gray-400 text-[15px] font-medium hover:text-white transition-colors flex items-center gap-2 no-underline">
            🏠 <span>Feed</span>
          </Link>
          <Link to="/shame-wall" className="text-purple-500 text-[15px] font-medium flex items-center gap-2 no-underline">
            😅 <span>Mural da Vergonha</span>
          </Link>
          <Link to="/ranking" className="text-gray-400 text-[15px] font-medium hover:text-white transition-colors flex items-center gap-2 no-underline">
            🏆 <span>Ranking</span>
          </Link>
        </nav>
        <Link to="/perfil" className="flex items-center gap-3 px-3 py-1.5 bg-white/5 border border-white/10 rounded-[30px] cursor-pointer no-underline">
          <img 
            src={getDiscordAvatarUrl(user?.discord_id, user?.discord_avatar)} 
            alt={user?.discord_username}
            className="w-7 h-7 rounded-full"
          />
          <span className="text-sm font-medium text-white">{user?.discord_username}</span>
        </Link>
      </header>

      <div className="max-w-[1600px] mx-auto px-10 pt-[100px] pb-10 relative z-[1]">
        
        {/* Hero Section: Featured Clip */}
        {featuredVideo && (
          <div className="grid grid-cols-[2fr_1fr] gap-8 mb-12 bg-[#111827]/40 border border-purple-500/15 rounded-3xl p-8 backdrop-blur-[10px]">
            <div className="flex flex-col justify-center">
              <div className="bg-gradient-to-r from-pink-500 to-purple-500 text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase w-fit mb-4 shadow-[0_4px_15px_rgba(236,72,153,0.3)]">
                🔥 Em Alta #1
              </div>
              <h1 className="text-4xl font-extrabold mb-4 leading-tight bg-gradient-to-r from-white to-purple-400 bg-clip-text text-transparent">
                {featuredVideo.title || 'Sem título'}
              </h1>
              <p className="text-gray-400 text-base leading-relaxed mb-6">
                {featuredVideo.description || 'Os momentos mais engraçados e constrangedores do OASIS.'}
              </p>
              
              <div className="flex gap-5 mb-8">
                <div className="bg-white/5 px-5 py-2.5 rounded-xl text-center">
                  <span className="block text-lg font-bold text-white">{formatNumber(featuredVideo.views_count || featuredVideo.likes_count || 0)}</span>
                  <span className="text-xs text-gray-400">Visualizações</span>
                </div>
                <div className="bg-white/5 px-5 py-2.5 rounded-xl text-center">
                  <span className="block text-lg font-bold text-white">{formatNumber(featuredVideo.likes_count || 0)}</span>
                  <span className="text-xs text-gray-400">Curtidas</span>
                </div>
                <div className="bg-white/5 px-5 py-2.5 rounded-xl text-center">
                  <span className="block text-lg font-bold text-white">{featuredVideo.comments_count || 0}</span>
                  <span className="text-xs text-gray-400">Comentários</span>
                </div>
              </div>

              <button 
                onClick={() => handleVideoClick(featuredVideo.id)}
                className="bg-white text-[#0a0a14] border-0 px-8 py-4 rounded-xl font-bold text-base cursor-pointer transition-transform hover:-translate-y-0.5 flex items-center justify-center gap-2.5 shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              >
                ▶ Assistir Agora
              </button>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.3)]">
              <img 
                src={getYouTubeThumbnail(extractYouTubeId(featuredVideo.youtube_url)) || '/placeholder.jpg'} 
                alt="Featured Video"
                className="w-full h-full object-cover min-h-[400px] block"
              />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-purple-500/90 rounded-full flex items-center justify-center text-[30px] opacity-100">
                ▶
              </div>
            </div>
          </div>
        )}

        {/* Filter Bar */}
        <div className="sticky top-20 bg-[#0a0a14]/95 backdrop-blur-[15px] p-5 rounded-2xl border border-purple-500/15 mb-8 flex justify-between items-center z-[900] shadow-[0_10px_30px_rgba(0,0,0,0.2)]">
          <div className="relative w-[450px]">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar clipes, jogos ou usuários..."
              className="w-full bg-white/5 border border-purple-500/20 rounded-xl px-5 py-3.5 pr-11 text-white outline-none transition-all text-[15px] focus:border-purple-500 focus:bg-purple-500/5 focus:shadow-[0_0_15px_rgba(139,92,246,0.1)]"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">🔍</span>
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-white/5 border border-purple-500/20 text-white px-5 py-3 rounded-xl outline-none cursor-pointer text-sm min-w-[180px]"
          >
            <option value="recent">Mais Recentes</option>
            <option value="views">Mais Vistos</option>
            <option value="likes">Mais Curtidos</option>
            <option value="trending">Em Alta</option>
          </select>
        </div>

        {/* Video Grid */}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-6">
          {videos.map((video) => {
            const videoId = extractYouTubeId(video.youtube_url);
            return (
              <div
                key={video.id}
                onClick={() => handleVideoClick(video.id)}
                className="bg-[#111827]/40 border border-white/5 rounded-2xl overflow-hidden transition-all cursor-pointer relative hover:-translate-y-2 hover:border-purple-500/40 hover:shadow-[0_10px_30px_rgba(0,0,0,0.3)]"
              >
                <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
                  <img
                    src={getYouTubeThumbnail(videoId) || '/placeholder.jpg'}
                    alt="Thumb"
                    className="absolute top-0 left-0 w-full h-full object-cover transition-transform hover:scale-105 hover:opacity-80"
                  />
                  <div className="absolute bottom-2.5 right-2.5 bg-black/80 text-white px-1.5 py-0.5 rounded text-xs font-semibold">
                    {/* Duração não disponível no modelo atual */}
                  </div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 scale-75 w-12 h-12 bg-purple-500/90 rounded-full flex items-center justify-center text-xl opacity-0 transition-all hover:opacity-100 hover:scale-100">
                    ▶
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-[15px] font-semibold mb-2 text-white leading-snug line-clamp-2">
                    {video.title || 'Sem título'}
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <img
                        src={getDiscordAvatarUrl(video.author_discord_id, video.author_avatar)}
                        alt={video.author_username}
                        className="w-6 h-6 rounded-full border border-purple-500"
                      />
                      <span className="text-[13px] text-gray-400">{video.author_username}</span>
                    </div>
                    <div className="flex gap-2.5 text-xs text-gray-500">
                      <span>👁️ {formatNumber(video.views_count || video.likes_count || 0)}</span>
                      <span>❤️ {formatNumber(video.likes_count || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {videos.length === 0 && !loading && (
          <div className="text-center py-20 text-gray-400">
            <p className="text-xl">Nenhum vídeo encontrado</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShameWall;

